/* eslint-disable jsx-a11y/alt-text */
import React, { useMemo } from 'react'
import { Font, Image, Text, View } from '@react-pdf/renderer'

import { rotorReferencesStyles as styles } from '@/components/organisms/Viewer/styles'
import {
  formatDimensions_Rotor,
  getByMetaValue,
  getCustomProductName,
  getQuantityLabel,
  getTranslation,
} from '@/utils'
import { imgPath } from '@/services/adapters'

// Register the Inter font
Font.register({
  family: 'Inter',
  fonts: [
    {
      src: '/fonts/Inter_18pt-ExtraLight.ttf',
      fontWeight: 'light',
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter_18pt-ExtraLightItalic.ttf',
      fontWeight: 'light',
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter_18pt-Regular.ttf',
      fontWeight: 'normal',
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter_18pt-Medium.ttf',
      fontWeight: 'medium',
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter_18pt-SemiBold.ttf',
      fontWeight: 'semibold',
      fontStyle: 'normal',
    },
  ],
})

type TableRowProps = {
  item: Record<string, React.ReactNode>
  rowStyle?: Record<string, any>
}

const TableRow = ({ item, rowStyle }: TableRowProps) => {
  const { image, customID, designation, quantity, dimensions, description } =
    item

  return (
    <View style={{ ...styles.row, ...rowStyle }}>
      <View style={styles.imageContainer}>{image}</View>
      <View style={{ ...styles.item, ...styles.code }}>{customID}</View>
      <View style={{ ...styles.item, ...styles.designation }}>
        {designation}
      </View>
      <View style={{ ...styles.item, ...styles.unit }}>{quantity}</View>
      <View style={{ ...styles.item, ...styles.dimensions }}>{dimensions}</View>
      <View style={{ ...styles.item, ...styles.designation }}>
        {description}
      </View>
    </View>
  )
}

type InventoryItemProps = {
  product: any
  locale: string
  index: number
  fallbackLocales?: readonly string[]
}

const InventoryItem = ({
  product,
  locale,
  index,
  fallbackLocales,
}: InventoryItemProps) => {
  const { pictures } = product
  const hyphenationCallback =
    locale === 'nl' ? (word: string) => [word] : undefined

  const imageUrl = useMemo(
    () => imgPath(pictures?.[0], 'thumbnail'),
    [pictures],
  )

  const designationLabel = useMemo(
    () => getCustomProductName(product, locale, fallbackLocales),
    [fallbackLocales, product, locale],
  )

  const quantityLabel = useMemo(() => {
    return getQuantityLabel(product)
  }, [product])

  const dimensionsLabel = useMemo(
    () => formatDimensions_Rotor(product)?.label,
    [product],
  )

  const customIDLabel = useMemo(
    () => getByMetaValue(product, 'isID'),
    [product],
  )

  const descriptionLabel = useMemo(
    () => getTranslation(product, 'description', locale, fallbackLocales),
    [fallbackLocales, locale, product],
  )

  const rowStyle = {
    paddingVertical: 2,
    ...(index % 2 === 0 && { backgroundColor: '#d9d9d9' }),
  }

  const item = {
    image: <Image src={`${imageUrl}`} style={styles.image} />,
    customID: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.bold}>
        {customIDLabel}
      </Text>
    ),
    designation: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.text}>
        {designationLabel}
      </Text>
    ),
    quantity: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.centered}>
        {quantityLabel}
      </Text>
    ),
    dimensions: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.centered}>
        {dimensionsLabel}
      </Text>
    ),
    description: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.text}>
        {descriptionLabel}
      </Text>
    ),
  }
  return <TableRow item={item} rowStyle={rowStyle} />
}

type RotorReferencesProps = {
  page: any
  locale: string
  isLastPage: boolean
  t: (key: string, values?: Record<string, unknown>) => string
  fallbackLocales?: readonly string[]
}

export const RotorReferences = ({
  page,
  locale,
  isLastPage,
  t,
  fallbackLocales,
}: RotorReferencesProps) => {
  const hyphenationCallback =
    locale === 'nl' ? (word: string) => [word] : undefined
  const headerItem = {
    image: (
      <Text
        hyphenationCallback={hyphenationCallback}
        style={{ ...styles.bold, paddingLeft: 0 }}
      >
        {t('Preview.RotorReferences.photo')}
      </Text>
    ),
    customID: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.bold}>
        {t('Preview.RotorReferences.code')}
      </Text>
    ),
    designation: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.bold}>
        {t('Preview.RotorReferences.designation')}
      </Text>
    ),
    quantity: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.bold}>
        {t('Preview.RotorReferences.quantity')}
      </Text>
    ),
    dimensions: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.bold}>
        {t('Preview.RotorReferences.dimensions')}
      </Text>
    ),
    description: (
      <Text hyphenationCallback={hyphenationCallback} style={styles.bold}>
        {t('Preview.RotorReferences.description')}
      </Text>
    ),
  }

  return (
    <View style={styles.list}>
      <View style={styles.titleContainer}>
        <Text hyphenationCallback={hyphenationCallback} style={styles.title}>
          {t('Preview.RotorReferences.title')}
        </Text>
      </View>
      <TableRow item={headerItem} rowStyle={{ paddingVertical: 4 }} />
      {page.items?.map((item, index) => (
        <InventoryItem
          key={item.id}
          product={item}
          locale={locale}
          index={index}
          fallbackLocales={fallbackLocales}
        />
      ))}
      {!!isLastPage && (
        <View
          style={{ paddingVertical: 2, borderBottom: '0.75 solid #000000' }}
        />
      )}
    </View>
  )
}
